if (!customElements.get('nosto-placement-tabs')) {
  class NostoPlacementTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.panels = [];
      this.titleObservers = [];
      this.sharedControls = null;
    }

    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.sharedControls = this.querySelector('.nosto-placement-tabs__controls');

      this.observePlacementTitles();

      if (this.tabs.length <= 1) return;

      const defaultIndex = Number.parseInt(this.dataset.defaultIndex || '0', 10);
      const startIndex = Number.isNaN(defaultIndex)
        ? 0
        : Math.min(defaultIndex, this.tabs.length - 1);

      this.activateTab(startIndex, false);

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(index));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index));
      });
    }

    disconnectedCallback() {
      this.titleObservers.forEach((observer) => observer.disconnect());
      this.titleObservers = [];
    }

    getPanelForTab(tab) {
      const blockId = tab?.dataset.blockId;
      if (!blockId) return null;
      return this.panels.find((panel) => panel.dataset.blockId === blockId) || null;
    }

    getTabForPanel(panel) {
      const blockId = panel?.dataset.blockId;
      if (!blockId) return null;
      return this.tabs.find((tab) => tab.dataset.blockId === blockId) || null;
    }

    activateTab(index, focusTab = true) {
      const tab = this.tabs[index];
      if (!tab) return;

      const activePanel = this.getPanelForTab(tab);

      this.tabs.forEach((currentTab) => {
        const isSelected = currentTab === tab;
        currentTab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        currentTab.classList.toggle('is-active', isSelected);
        currentTab.tabIndex = isSelected ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        if (panel === activePanel) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      this.syncActivePanelSlider(activePanel);

      if (focusTab) {
        tab.focus();
      }
    }

    handleTabKeydown(event, index) {
      const keyActions = {
        ArrowLeft: () => (index === 0 ? this.tabs.length - 1 : index - 1),
        ArrowUp: () => (index === 0 ? this.tabs.length - 1 : index - 1),
        ArrowRight: () => (index === this.tabs.length - 1 ? 0 : index + 1),
        ArrowDown: () => (index === this.tabs.length - 1 ? 0 : index + 1),
        Home: () => 0,
        End: () => this.tabs.length - 1,
      };

      const getNextIndex = keyActions[event.key];
      if (!getNextIndex) return;

      event.preventDefault();
      this.activateTab(getNextIndex());
    }

    syncActivePanelSlider(panel) {
      if (!panel) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const slider = panel.querySelector('nosto-slider');
          if (!slider) return;

          const isInitialized =
            typeof window.jQuery !== 'undefined' &&
            window.jQuery(slider).hasClass('slick-initialized');

          if (isInitialized) {
            slider.refreshSlider();
          } else {
            slider.ensureInitialized();
          }

          this.bindSharedArrows(slider);
        });
      });
    }

    bindSharedArrows(slider) {
      if (!this.sharedControls || !slider || typeof window.jQuery === 'undefined') return;

      const $slider = window.jQuery(slider);
      if (!$slider.hasClass('slick-initialized')) return;

      const prevArrow = this.sharedControls.querySelector('.controls__arrow__prev');
      const nextArrow = this.sharedControls.querySelector('.controls__arrow__next');
      if (!prevArrow || !nextArrow) return;

      $slider.slick('slickSetOption', 'prevArrow', window.jQuery(prevArrow), true);
      $slider.slick('slickSetOption', 'nextArrow', window.jQuery(nextArrow), true);
    }

    syncTabLabel(panel, tab) {
      const titleEl = panel.querySelector('.nosto-placement__title');
      const text = titleEl?.textContent?.trim();
      if (!text) return;

      const labelSpan = tab.querySelector('.nosto-placement-tabs__tab-label');
      if (labelSpan) {
        labelSpan.textContent = text;
      }

      tab.setAttribute('aria-label', text);
      titleEl.setAttribute('aria-hidden', 'true');
    }

    observePlacementTitles() {
      this.panels.forEach((panel) => {
        const tab = this.getTabForPanel(panel);
        const nostoEl = panel.querySelector('.nosto_element');
        if (!nostoEl || !tab) return;

        const sync = () => {
          const titleEl = panel.querySelector('.nosto-placement__title');
          if (!titleEl?.textContent?.trim()) return;

          this.syncTabLabel(panel, tab);
          return true;
        };

        if (sync()) return;

        const observer = new MutationObserver(() => {
          if (sync()) {
            observer.disconnect();
          }
        });

        observer.observe(nostoEl, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.titleObservers.push(observer);
      });
    }
  }

  customElements.define('nosto-placement-tabs', NostoPlacementTabs);
}
